import type { Transaction, WorkerSchema } from "../types";
import { formatAmount, formatThaiDatetime } from "./formatters";
import { getDisplayText, getDisplayValue } from "./display";
import { resolveSlipMatchedAmount } from "./incomeDerived";
import {
  filterHistoryDetailsForDisplay,
  filterHistoryEntriesForDisplay,
  formatHistoryActorLabel,
} from "./historyDisplay";
import { authHeadersForJson } from "./clientAuth";

export type TransactionHistoryEntryType =
  | "recorded"
  | "edited"
  | "transfer"
  | "opened"
  | "matched"
  | "unmatched"
  | "moved"
  | "split"
  | "undone";

export type TransactionHistorySummary = {
  headline: string;
  rows: Array<{ label: string; value: string; emphasize?: boolean }>;
  explanation: string;
};

export type TransactionHistoryEntry = {
  id: string;
  type: TransactionHistoryEntryType;
  title: string;
  description: string;
  actorId: string;
  actorName: string;
  channelLabel?: string;
  occurredAt: string;
  details?: string[];
  isLatest?: boolean;
  reversible?: boolean;
  availableActions?: string[];
};

export class HistoryLoadError extends Error {
  constructor(message = "โหลดประวัติไม่สำเร็จ") {
    super(message);
    this.name = "HistoryLoadError";
  }
}

type ApiHistoryEntry = {
  id: string;
  type: string;
  title: string;
  description: string;
  actor_name: string;
  channel_label?: string;
  occurred_at: string;
  details?: string[];
  is_latest?: boolean;
  reversible?: boolean;
  available_actions?: string[];
};

function formatOccurredAt(iso: string): string {
  if (!iso?.trim()) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return formatThaiDatetime(iso) || iso;
  return formatThaiDatetime(d.toISOString()) || iso;
}

function mapApiEntry(row: ApiHistoryEntry): TransactionHistoryEntry {
  const rawActor = (row.actor_name || "").trim();
  return {
    id: row.id,
    type: (row.type as TransactionHistoryEntryType) || "recorded",
    title: row.title,
    description: row.description,
    actorId: rawActor,
    actorName: formatHistoryActorLabel(rawActor) || "—",
    channelLabel: row.channel_label || undefined,
    occurredAt: formatOccurredAt(row.occurred_at),
    details: filterHistoryDetailsForDisplay(row.details),
    isLatest: row.is_latest,
    reversible: row.reversible === true,
    availableActions: Array.isArray(row.available_actions)
      ? row.available_actions.map(String)
      : [],
  };
}

export async function fetchTransactionHistory(
  recordId: string,
  workspaceId: string,
): Promise<TransactionHistoryEntry[]> {
  const qs = new URLSearchParams({ workspace_id: workspaceId });
  const res = await fetch(
    `/api/v1/transactions/${encodeURIComponent(recordId)}/history?${qs}`,
    {
      headers: {
        Accept: "application/json",
        ...authHeadersForJson(),
      },
    },
  );
  if (!res.ok) {
    throw new HistoryLoadError(`โหลดประวัติไม่สำเร็จ (${res.status})`);
  }
  const data = (await res.json()) as { entries?: ApiHistoryEntry[] };
  return filterHistoryEntriesForDisplay(data.entries ?? []).map(mapApiEntry);
}

export type TransactionUndoImpact = {
  ok: boolean;
  blocked?: boolean;
  reason?: string;
  target_operation_id?: string;
  operation_type?: string;
  title?: string;
  description?: string;
  details?: string[];
  channel_label?: string;
  occurred_at?: string;
  summary_lines?: string[];
};

const UNDO_BLOCK_REASON_TH: Record<string, string> = {
  ALREADY_UNDONE: "รายการนี้ถูกย้อนกลับไปแล้ว",
  LATER_OPERATION_EXISTS:
    "มีการแก้ไขใหม่กว่าบนยอดที่เกี่ยวข้องแล้ว — ไม่สามารถย้อนรายการนี้ได้",
  not_undoable: "รายการนี้ไม่สามารถย้อนกลับได้",
  target_operation_not_found: "ไม่พบรายการที่ย้อนกลับ",
  target_operation_id_required: "ไม่พบรายการที่ย้อนกลับ",
};

export function formatTransactionUndoBlockMessage(impact: {
  reason?: string;
  summary_lines?: string[];
} | null | undefined): string {
  const fromApi = impact?.summary_lines?.find((line) => String(line || "").trim());
  if (fromApi) return String(fromApi).trim();
  const reason = String(impact?.reason || "").trim();
  if (reason && UNDO_BLOCK_REASON_TH[reason]) return UNDO_BLOCK_REASON_TH[reason];
  if (reason && !/^[A-Z0-9_]+$/.test(reason)) return reason;
  return "ไม่สามารถย้อนกลับรายการนี้ได้";
}

function createOperationKey(prefix: string): string {
  const rand =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  return `${prefix}:${rand}`;
}

export async function fetchTransactionUndoImpact(args: {
  ownerUserId: string;
  targetOperationId: string;
}): Promise<TransactionUndoImpact> {
  const qs = new URLSearchParams({
    owner_user_id: args.ownerUserId,
    target_operation_id: args.targetOperationId,
  });
  const res = await fetch(
    `/api/v1/payments/corrections/undo/impact?${qs}`,
    {
      headers: {
        Accept: "application/json",
        ...authHeadersForJson(),
      },
    },
  );
  const data = (await res.json().catch(() => ({}))) as TransactionUndoImpact & {
    detail?: string;
    error?: string;
  };
  if (!res.ok) {
    throw new Error(
      typeof data.detail === "string"
        ? data.detail
        : typeof data.error === "string"
          ? data.error
          : `โหลดผลกระทบการย้อนกลับไม่สำเร็จ (${res.status})`,
    );
  }
  return data;
}

export async function postTransactionUndoCorrection(args: {
  ownerUserId: string;
  workspaceId: string;
  targetOperationId: string;
}): Promise<void> {
  const res = await fetch("/api/v1/payments/corrections/undo", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...authHeadersForJson(),
    },
    body: JSON.stringify({
      owner_user_id: args.ownerUserId,
      workspace_id: args.workspaceId,
      target_operation_id: args.targetOperationId,
      operation_key: createOperationKey("undo"),
      channel: "web",
    }),
  });
  const data = (await res.json().catch(() => ({}))) as {
    detail?: string;
    error?: string;
  };
  if (!res.ok) {
    throw new Error(
      typeof data.detail === "string"
        ? data.detail
        : typeof data.error === "string"
          ? data.error
          : `ย้อนกลับไม่สำเร็จ (${res.status})`,
    );
  }
}

export function buildTransactionHistorySummary(
  record: Transaction,
  schema: WorkerSchema,
): TransactionHistorySummary {
  const isExpense = schema.workerId === "bill-lin-expense";
  const amountCol = schema.columns.find((c) => c.type === "amount");
  const amountRaw = amountCol ? record[amountCol.id] : null;
  const amount = formatAmount(getDisplayValue(amountRaw) as number | null | undefined);

  const statusCol = schema.columns.find((c) => c.type === "status");
  const status = statusCol ? getDisplayText(record[statusCol.id]) : "—";

  if (isExpense) {
    const vendor = getDisplayText(record.vendor) || "—";
    const category = getDisplayText(record.category) || "—";
    return {
      headline: "สรุปรายจ่ายตอนนี้",
      rows: [
        { label: "จำนวนเงิน", value: amount, emphasize: true },
        { label: "ร้านค้า", value: vendor },
        { label: "หมวดหมู่", value: category },
        { label: "สถานะ", value: status },
      ],
      explanation: "รายการรายจ่ายที่บันทึกในระบบ",
    };
  }

  const customer =
    getDisplayText(record.customer_name) ||
    getDisplayText(record.sender_name) ||
    "—";

  const slipMatched = resolveSlipMatchedAmount(record);
  const rows: TransactionHistorySummary["rows"] = [
    { label: "ยอดโอน", value: amount, emphasize: true },
  ];
  if (slipMatched != null) {
    rows.push({
      label: "ยอดจับคู่จากสลิปค้าง",
      value: formatAmount(slipMatched),
      emphasize: true,
    });
  }
  rows.push(
    { label: "ลูกค้า/ผู้โอน", value: customer },
    { label: "สถานะ", value: status },
  );

  return {
    headline: "สรุปรายรับตอนนี้",
    rows,
    explanation: "",
  };
}

export function pickLatestTransactionHistoryMeta(
  entries: TransactionHistoryEntry[],
): { label: string; actorName: string; occurredAt: string } | null {
  const latest = entries.find((entry) => entry.isLatest) ?? entries[0];
  if (!latest || latest.occurredAt === "—") return null;
  const actorName = formatHistoryActorLabel(latest.actorName);
  return {
    label: latest.title,
    actorName: actorName || "",
    occurredAt: latest.occurredAt,
  };
}
